import {Button, Col, FormGroup, Input, Label, Row, UncontrolledTooltip} from 'reactstrap';
import classNames from 'classnames';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import paginationFactory, {
    PaginationListStandalone,
    PaginationProvider,
    PaginationTotalStandalone,
    SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import BootstrapTable from 'react-bootstrap-table-next';
import React, {useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import {PaginationConfig} from './PaginationConfig';
import Spinner from '../Spinner';
import {Link} from 'react-router-dom';
import PerfectScrollbar from "react-perfect-scrollbar";
import filterFactory from 'react-bootstrap-table2-filter';
import {generateRandomString} from '../../../../shared/helper'
import {useDebounce} from '../../../../shared/hooks/useDebounce';
import Select from 'react-select';

type TableProps = {
    data: [],
    columns: [],
    keyField: string,
    totalSize: number,
    pageable: Boolean,
    offlineSortable: Boolean,
    offlinePageable: Boolean,
    page: number,
    sizePerPage: number,
    searchText: str,
    buttons: [],
    selected: [],
    clickToSelect: boolean,
    onTableChange: PropTypes.func,
    onRowSelected: PropTypes.func,
    onSelectAll: PropTypes.func,
    onRowHover?: PropTypes.func,
    onRowBlur?: PropTypes.func,
    isTableScroll: false,
    loading: Boolean,
    refresh: Boolean,
    onDownload: PropTypes.func,
    onExport: PropTypes.func,
    onShare: PropTypes.func,

    hideShare: Boolean,
};

const TableAdditionalActions = ({
                                    buttons, refresh, selected,
                                    page, sizePerPage, searchText, sort, filters,
                                    onTableChange, onShare, hideShare, onDownload, onExport, hasModifyPermission
                                }) => {

    buttons = buttons || [];
    selected = selected || [];
    const _buttons = buttons.map((btn, idx) => {
        const ActionButton = (
            <Button hidden={btn.hidden === true} id={'btn-table-action' + idx} key={idx} size="md" className={'ml-2'}
                    onClick={btn.onClick} color={btn.color}>
                <i className={btn.classNames}/>
                <UncontrolledTooltip placement='top' target={'btn-table-action' + idx}>
                    {btn.title}
                </UncontrolledTooltip>
            </Button>
        );
        if (btn.redirect) {
            return (
                <Link to={btn.redirect} key={idx}>
                    {ActionButton}
                </Link>
            );
        }
        return ActionButton
    });

    const handleShare = () => {
        onShare(selected);
    };

    const handleDownload = () => {
        onDownload(selected);
    };

    const handleExport = () => {
        onExport(selected);
    };

    let sortOrder
    let sortField
    if (sort) {
        sortOrder = sort.order
        sortField = sort.field
    }
    const handleRefresh = () => {
        onTableChange('refresh', {page, sizePerPage, searchText, sortOrder, sortField, filters});
    };

    return (
        <React.Fragment>
            {_buttons}
            <Button id='share-data' size="md" className='ml-2' onClick={handleShare}
                    hidden={!hasModifyPermission || !onShare || !selected || selected.length < 1}>
                <i className='mdi mdi-share'/>
                <UncontrolledTooltip placement='top' target='share-data'>
                    Share
                </UncontrolledTooltip>
            </Button>
            <Button id='download-data' size="md" className={'ml-2'} onClick={handleDownload}
                    hidden={!selected || selected.length < 1 || !onDownload || !hasModifyPermission}>
                <i className='mdi mdi-download-multiple'/>
                <UncontrolledTooltip placement='top' target='download-data'>
                    Download
                </UncontrolledTooltip>
            </Button>
            <Button id='export-data' size="md" className={'ml-2'} onClick={handleExport}
                    hidden={!selected || selected.length < 1 || !onExport || !hasModifyPermission}>
                <i className='mdi mdi-export'/>
                <UncontrolledTooltip placement='top' target='export-data'>
                    Export
                </UncontrolledTooltip>
            </Button>
            <Button id='refresh-data' size="md" className={'ml-2'} onClick={handleRefresh} hidden={!refresh}>
                <i className='mdi mdi-refresh'/>
                <UncontrolledTooltip placement='top' target='refresh-data'>
                    Refresh
                </UncontrolledTooltip>
            </Button>

            {/* <UncontrolledButtonDropdown className="ml-2">
                <DropdownToggle>
                    <i className='mdi mdi-dots-vertical' />
                </DropdownToggle>
                <DropdownMenu>
                    <DropdownItem onClick={handleRefresh}><i
                        className='mdi mdi-refresh mr-2' />Refresh</DropdownItem>
                    {_actions}
                </DropdownMenu>
            </UncontrolledButtonDropdown> */}
        </React.Fragment>
    );
};

const TableRecordsTotal = (from, to, size) => {
    return (
        <label className="react-bootstrap-table-pagination-total mt-1">
            Showing {from} to {to} of {size}
        </label>
    );
};

const TableSizePerPage = ({options, currSizePerPage, onSizePerPageChange}) => {
    const handleChangePage = (option) => {
        onSizePerPageChange(option.value)
    }

    const pageOptions = options.map(option => ({
        label: option.page,
        value: option.page
    }))

    const defaultOption = {
        label: currSizePerPage,
        value: currSizePerPage
    }

    return (
        <FormGroup className="dropdown-inline">
            <Label>Display</Label>
            <Select options={pageOptions}
                    menuPosition="top"
                    onChange={handleChangePage}
                    value={defaultOption}
                    className='dropdown-list' classNamePrefix='dropdown-list'
            />
        </FormGroup>
    );
};

const SelectionRenderer = ({mode, rowIndex, ...rest}) => {
    const id = `cb-${rowIndex}-${generateRandomString()}`;

    const onChecked = (e) => {
        e.preventDefault()
    };

    const stopPropagation = (event) => {
        event.stopPropagation();
        document.getElementById(id).checked = !rest.checked;
    };

    return (
        <div className="custom-checkbox custom-control">
            <Input type={mode} id={id} checked={rest.checked}
                   className="custom-control-input" onChange={onChecked}/>
            <label className="custom-control-label cursor-pointer" htmlFor={id} onClick={stopPropagation}/>
        </div>
    );
};

const DisabledSelectionRender = ({mode, rowIndex, ...rest}) => {
    return (
        <div className="">
        </div>
    );
};

const Table = (parentProps: TableProps) => {
    const searchInput = useRef(null);
    const {
        refresh, search, pageable, offlineSortable, offlinePageable,
        page, sizePerPage, searchText, sort, filters, loading, totalSize,
        onShare, onDownload, onExport, onTableChange, data, columns, keyField, selected, singleSelection, hideShare
    } = parentProps;
    const tableProps = {data: data || [], columns: columns || [], keyField: keyField || 'id'};
    const noData = tableProps.data.length === 0

    let defaultSorted = []
    if (!!sort) {
        defaultSorted = [{dataField: sort.field, order: sort.order}]
    }

    let selectRow
    if (singleSelection) {
        selectRow = {
            mode: 'radio',
            hideSelectColumn: true,
            clickToSelect: true,
            selected: selected,
            onSelect: parentProps.onRowSelected,
        };
    } else {
        selectRow = {
            mode: 'checkbox',
            clickToSelect: parentProps.clickToSelect,
            selectionHeaderRenderer: SelectionRenderer,
            selectionRenderer: SelectionRenderer,
            hideSelectAll: false,
            selected: selected,
            onSelect: parentProps.onRowSelected,
            onSelectAll: parentProps.onSelectAll,
        };
    }

    if (parentProps.disableSelect) {
        selectRow = {
            mode: 'checkbox',
            clickToSelect: false,
            selectionRenderer: DisabledSelectionRender,
            hideSelectAll: true,
            selected: selected,
            disabled: true,
            onSelect: parentProps.onRowSelected,
            onSelectAll: parentProps.onSelectAll,
        }
    }

    const rowEvents = {
        onMouseEnter: (e, row, rowIndex) => {
            if (parentProps.onRowHover && e.target.className.indexOf('preview-on-hover') > -1) {
                parentProps.onRowHover(row);
            }
        },
        onMouseLeave: (e, row, rowIndex) => {
            if (parentProps.onRowBlur) {
                parentProps.onRowBlur(row);
            }
        }
    };

    const rowClasses = (row) => {
        if (selected && row && selected.includes(row[keyField])) {
            return 'selected'
        }
    };

    const preventActionsWhenLoading = (event) => {
        if (loading) {
            event.preventDefault()
        }
    }

    const handleSearch = useCallback(useDebounce(_ => {
        if (searchInput.current && parentProps.onSearch) {
            parentProps.onSearch(searchInput.current.value);
        }
    }, 2000), []);

    return (
        <div onKeyDown={preventActionsWhenLoading} onPaste={preventActionsWhenLoading}
             onClick={preventActionsWhenLoading}>
            <PaginationProvider
                bootstrap4
                pagination={paginationFactory({
                    ...PaginationConfig,
                    page,
                    sizePerPage,
                    totalSize,
                    custom: true,
                    paginationTotalRenderer: TableRecordsTotal,
                    sizePerPageRenderer: TableSizePerPage,
                })}
                {...tableProps}>
                {({paginationProps, paginationTableProps}) => (
                    <ToolkitProvider {...tableProps} search>
                        {props => (
                            <React.Fragment>
                                <Row className="mb-2">
                                    <Col hidden={!parentProps.children || parentProps.children.length === 0} md='auto'>
                                        {parentProps.children}
                                    </Col>
                                    <Col hidden={!search} md='auto'>
                                        <FormGroup>
                                            <Label className='mr-2' for='searchBar0'>Search</Label>
                                            <Label>
                                                <input className='form-control' onChange={handleSearch}
                                                       placeholder='Search content...' id='searchBar0'
                                                       defaultValue={searchText || ''} ref={searchInput}/>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col hidden={!pageable} md='auto'>
                                        <SizePerPageDropdownStandalone {...paginationProps} />
                                    </Col>
                                    <Col md='auto' className="flex-grow-1 text-right">
                                        <TableAdditionalActions
                                            buttons={parentProps.buttons}
                                            refresh={refresh}
                                            selected={selected}
                                            onTableChange={onTableChange}
                                            onShare={onShare}
                                            onDownload={onDownload}
                                            onExport={onExport}
                                            hideShare={hideShare}
                                            page={page}
                                            sizePerPage={sizePerPage}
                                            searchText={searchText}
                                            sort={sort}
                                            filters={filters}
                                            hasModifyPermission={parentProps.hasModifyPermission}
                                        />
                                    </Col>
                                </Row>

                                <Row hidden={!pageable}>
                                    <Col>
                                        <PaginationTotalStandalone {...paginationProps}
                                                                   dataSize={parentProps.totalSize || 0}/>
                                    </Col>
                                    <Col className="react-bootstrap-table-pagination-list pagination-rounded">
                                        <PaginationListStandalone {...paginationProps} />
                                    </Col>
                                </Row>

                                <PerfectScrollbar className={classNames('ps', {'no-data-table': noData})}>

                                    <BootstrapTable
                                        {...props.baseProps}
                                        bordered={false}
                                        hover
                                        striped
                                        remote={{
                                            pagination: !offlineSortable,
                                            sort: !offlineSortable,
                                            filter: !offlinePageable,
                                            search: !offlinePageable
                                        }}

                                        defaultSorted={defaultSorted}

                                        filter={filterFactory()}
                                        filterPosition="top"

                                        classes="table-centered table-borderless"
                                        headerClasses="thead-default"
                                        filtersClasses="table-row-filter"

                                        wrapperClasses={
                                            (parentProps.isTableScroll ? "table-scroll " : " ") + parentProps.wrapperClasses
                                        }

                                        onTableChange={onTableChange}
                                        selectRow={selectRow}

                                        rowEvents={rowEvents}
                                        rowClasses={rowClasses}
                                        {...paginationTableProps}
                                    />

                                </PerfectScrollbar>

                                {noData &&
                                <div className="table-loading">
                                    {loading
                                        ? <Spinner type="bordered" color='primary'/>
                                        : <p className='font-italic'>No record found</p>
                                    }
                                </div>
                                }

                            </React.Fragment>
                        )}
                    </ToolkitProvider>
                )}
            </PaginationProvider>
        </div>
    );
};

export default Table;

